import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    showCloseButton?: boolean;
}

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = 'md', // md is the default size
    showCloseButton = true
}) => {
    // Disable document body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'max-w-sm',      // For alerts and compact notifications (~384px)
        md: 'max-w-md',      // Standard size (~448px)
        lg: 'max-w-2xl',      // Large forms (~672px)
        xl: 'max-w-4xl',      // Extra large dashboards (~896px)
        full: 'max-w-[90vw]'  // Near full-screen coverage
    };

    return createPortal(
        <div 
            className="fixed inset-0 flex items-center justify-center p-4 z-[9999]"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                margin: 0,
                padding: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.12)', // ultra premium transparent dark tint
                backdropFilter: 'blur(12px)', // high-performance glassmorphism blur
                WebkitBackdropFilter: 'blur(12px)', // Safari webkit support
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999,
                animation: 'fadeIn 0.2s ease-out'
            }}
        >
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 5px;
                    height: 5px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(148, 163, 184, 0.4);
                    border-radius: 9999px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(148, 163, 184, 0.6);
                }
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(148, 163, 184, 0.4) transparent;
                }
            `}</style>
            <div 
                className={`bg-white w-full ${sizeClasses[size]} max-h-[92vh] flex flex-col rounded-3xl shadow-2xl border border-slate-100/50 overflow-hidden transform scale-100 transition-all duration-300`}
                style={{
                    boxShadow: '0 25px 60px -15px rgba(15, 23, 42, 0.15), 0 0 50px rgba(0, 0, 0, 0.02)'
                }}
            >
                {/* Thin Sticky Header */}
                <div className="flex items-center justify-between px-6 py-2.5 border-b border-slate-100 bg-slate-50/60 sticky top-0 z-10 backdrop-blur-md">
                    <h3 className="text-sm font-bold text-slate-800 tracking-tight">{title}</h3>
                    {showCloseButton && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all duration-300 transform hover:rotate-180 hover:scale-115 focus:outline-none"
                            aria-label="Close modal"
                        >
                            <X className="h-4.5 w-4.5" />
                        </button>
                    )}
                </div>

                {/* Scrollable Modal Body (Only this part scrolls!) */}
                <div className="flex-1 overflow-y-auto p-6 min-h-0 text-sm text-slate-600 custom-scrollbar">
                    {children}
                </div>

                {/* Thin Sticky Footer */}
                {footer && (
                    <div className="px-6 py-2 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2.5 sticky bottom-0 z-10 backdrop-blur-md">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default Modal;
